// What the first-run funnel reports that nothing else already does (ENG-3796).
//
// MOST OF THE FUNNEL'S TELEMETRY IS NOT HERE. `main` owns this wizard's
// lifecycle under ENG-4305 (`analytics/domains/onboarding-lifecycle.ts`), which
// already names entry, per-screen views, step starts and submits, both exits
// and failures — and its own header explains that it exists so the wizard stops
// capturing outside `track()`. This file briefly re-implemented all of that
// under different names, including a second, incompatible definition of
// `onboarding_step_viewed`. That is gone; see hooks/use-onboarding-tracker.ts.
//
// What is left is the ONE event with no counterpart upstream, plus the one pure
// decision the connection test needs. Anything that can be said in ENG-4305's
// vocabulary is said there.
//
// Per rules: investigate-before-adding, code-comments-required,
// reference-rules-in-comments (ai.knowledge/global).

import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';

/**
 * This funnel's OWN event — the ONE name ENG-4305 has no equivalent for.
 *
 * Everything else it once defined is gone: entry, views, submits and both exits
 * are the lifecycle tracker's, and `onboarding_discovery_result_count`,
 * `onboarding_first_stream`, `onboarding_first_ptz_action` and
 * `onboarding_discovery_camera_picked` are listed as SUPERSEDED in
 * analytics/constants/onboarding-events.ts, each with the shared event it is
 * derived from. Reintroducing any of them fails that catalog's contract test.
 */
export const ONBOARDING_EVENTS = {
  /**
   * WHERE the camera's stream address came from.
   *
   * The funnel has four possible sources and they fail differently: a camera
   * that reported its own (NDI, RTSP), a model's registry template, an ONVIF
   * capabilities probe, or the manual form. An ONVIF camera has no address
   * until it is asked, so how often that probe is needed — and how often it
   * comes back empty — is the difference between "the wizard is broken" and
   * "this camera never answered". Both were indistinguishable in the field on
   * 2026-08-25 and cost hours to separate by hand.
   */
  ADDRESS_RESOLVED: EVENT_NAMES.ONBOARDING_ADDRESS_RESOLVED
} as const;

/** Where a camera's stream address came from. See ADDRESS_RESOLVED. */
export type OnboardingAddressSource =
  /** The discovery carried a complete video configuration (NDI, RTSP). */
  | 'discovery'
  /** The chosen model's registry template supplied the path and port. */
  | 'model_template'
  /** An ONVIF capabilities probe was run to fetch the stream URI. */
  | 'onvif_probe'
  /** The user typed it on the manual form. */
  | 'form';

/** Select the stream-address source before resolved video loses its provenance. */
export const selectAddressSource = (input: {
  readonly existingSource: OnboardingAddressSource | undefined;
  readonly cameraSource: 'discovery' | 'manual-entry';
  readonly hasTypedVideo: boolean;
  readonly hasTemplateVideo: boolean;
  readonly hasOnvifControl: boolean;
}): OnboardingAddressSource => {
  if (input.existingSource !== undefined) return input.existingSource;
  if (input.hasOnvifControl) return 'onvif_probe';
  if (input.cameraSource === 'manual-entry' && input.hasTypedVideo) return 'form';
  if (input.hasTemplateVideo) return 'model_template';

  return input.cameraSource === 'discovery' ? 'discovery' : 'form';
};

/**
 * Why the connection test failed, in priority order.
 *
 * Kept local and pure so the ORDER can be asserted. The order is the substance:
 * a camera that refused credentials also has no video, and reporting `no_video`
 * for it points the user at the address instead of the password. A plan-limit
 * block outranks everything because nothing about the camera was tested at all.
 *
 * The value rides `onboarding_failed` as a property. ENG-4305's tracker does
 * its own severity and recoverability classification from the raw error; this
 * is the wizard-specific bucket underneath it, which that classifier has no way
 * to know (it cannot tell a plan-limit refusal from a dead camera).
 */
export type OnboardingFailureReason =
  | 'plan_limit'
  | 'credentials_refused'
  | 'no_video'
  | 'control_unreachable'
  | 'add_failed';

export const selectFailureReason = (input: {
  readonly blocked: boolean;
  readonly authRefused: boolean;
  readonly streamFailed: boolean;
  readonly controlChecked: boolean;
  readonly controlFailed: boolean;
  readonly addFailed: boolean;
}): OnboardingFailureReason | undefined => {
  if (input.blocked) return 'plan_limit';
  if (input.authRefused) return 'credentials_refused';
  if (input.streamFailed) return 'no_video';
  if (input.controlChecked && input.controlFailed) return 'control_unreachable';
  if (input.addFailed) return 'add_failed';

  return undefined;
};
